/* eslint-disable eqeqeq */
import {
  UPDATE_EMPLOYEE_MAIN_HR_INFORMATION,
  READ_EMPLOYEE_MAIN_HR_INFORMATION,
  READ_EMPLOYEE_SPECIALIZATION_INFO,
  UPDATE_EMPLOYEE_SPECIALIZATION_INFO,
  EDUCATION_SPECIALIST_ID,
  SOFTWARE_ENGINEER_ID,
  PositionDropdown,
  EmployeePositionField,
  EmployeeTechnologyField,
} from 'domain/shared/employee';
import EditFormShowButton from 'domain/people/employee/shared/button/showEdit/EditFormShowButton';
import { COMPETENCY_LEVEL_WITHOUT_NA } from 'domain/shared/newVacancy';
import { TechnologyDropdown } from 'domain/shared/career';
import { TextField } from 'framework/ui/field';
import {
  EnumDropdown,
  ClearAfterUnmountWrapper,
  FormFieldsData,
  useFormContext,
  useInputHelper,
  useFormShowContext,
} from 'framework/form';
import { getTechnologyFilter } from 'domain/shared/career';
import { IconSectionRow, BigFormRow } from 'framework/ui/layout';
import { AppIconLabel } from 'framework/ui/mui-builder';
import { Box } from '@mui/material';
import { useAuthorization } from 'framework/authorization';
import FormActionButtons from 'domain/people/employee/shared/button/showEdit/FormActionButtons';
import { Features, featureToggles } from 'infrastructure/featureToggle';
import { POSITION_SLOT_LIST_READ } from 'domain/people/positionSlots/constants';
import EmployeePositionSlotField from '../positionSlot/EmployeePositionSlotField';

const UnitedTechnology = () => {
  return (
    <>
      <EmployeeTechnologyField
        label={<AppIconLabel icon="technology" tooltipTitle="Specialization" />}
        source="specializationInfo.technologyId"
      />
      <Box
        sx={{
          '.field__wrapper': {
            ml: '37px',
          },
        }}
      >
        <EmployeeTechnologyField label="" source="specializationInfo.secondaryTechnologyId" allowEmptySpace />
      </Box>
    </>
  );
};

type SpecializationContentProps = {
  onPositionSlotIdChange?: (positionSlotId: string | null) => void;
};

const SpecializationContent = ({ onPositionSlotIdChange }: SpecializationContentProps) => {
  const { isGranted } = useAuthorization();
  const { isEditMode } = useFormShowContext();
  const isPositionSlotVisible = Boolean(featureToggles[Features.POSITION_SLOTS]) && isGranted(POSITION_SLOT_LIST_READ);
  const { getValue } = useInputHelper({ source: 'specializationInfo.competencyLevel' });
  const { getRawOnChangeCallback } = useInputHelper({ source: 'positionId' });

  const rawOnChangeCallback = getRawOnChangeCallback() || (() => {});
  const competencyLevel = getValue() ?? '';

  const onChangePositionCallback = (newData: FormFieldsData) => {
    const positionId = newData?.positionId;
    if (positionId == SOFTWARE_ENGINEER_ID || positionId == EDUCATION_SPECIALIST_ID) {
      rawOnChangeCallback({
        positionId,
        specializationInfo: {
          technologyId: '',
          secondaryTechnologyId: '',
          competencyLevel,
        },
      });
    } else {
      rawOnChangeCallback(newData);
    }
  };

  const { data } = useFormContext();
  const { positionId } = data || {};

  return (
    <Box>
      <Box sx={{ position: 'absolute', right: '-15px', top: 0 }}>
        {isEditMode ? (
          <FormActionButtons sxWrapper={{ justifyContent: 'flex-end', ml: 0 }} />
        ) : (
          <EditFormShowButton
            requiredPermission={[UPDATE_EMPLOYEE_MAIN_HR_INFORMATION, UPDATE_EMPLOYEE_SPECIALIZATION_INFO]}
          />
        )}
      </Box>

      {isEditMode ? (
        <BigFormRow>
          <PositionDropdown source="positionId" label="Position" onChangeCallback={onChangePositionCallback} />
          {[
            <ClearAfterUnmountWrapper
              isVisible={employee =>
                employee.positionId == SOFTWARE_ENGINEER_ID || employee.positionId == EDUCATION_SPECIALIST_ID
              }
            >
              <TechnologyDropdown
                customReduce={getTechnologyFilter(positionId)}
                deps={[positionId]}
                source="specializationInfo.technologyId"
                label="Technology"
              />
            </ClearAfterUnmountWrapper>,

            <ClearAfterUnmountWrapper isVisible={employee => employee.positionId == SOFTWARE_ENGINEER_ID}>
              <TechnologyDropdown
                source="specializationInfo.secondaryTechnologyId"
                label="Secondary technology"
                customReduce={getTechnologyFilter(positionId)}
              />
            </ClearAfterUnmountWrapper>,

            <EnumDropdown
              source="specializationInfo.competencyLevel"
              options={COMPETENCY_LEVEL_WITHOUT_NA}
              label="Competency level"
              isVisible={isGranted(UPDATE_EMPLOYEE_SPECIALIZATION_INFO)}
            />,
          ]}
        </BigFormRow>
      ) : (
        <Box
          sx={{
            '& .field__wrapper': {
              alignItems: 'flex-start',
              '& > div': {
                flexGrow: 0,
              },
            },
          }}
        >
          <IconSectionRow colXl={4} colMd={4} containerProps={{ alignItems: 'flex-start' }}>
            {isGranted(READ_EMPLOYEE_MAIN_HR_INFORMATION) && (
              <EmployeePositionField
                source="positionId"
                label={<AppIconLabel icon="specialization" tooltipTitle="Position" />}
              />
            )}

            {isGranted([READ_EMPLOYEE_SPECIALIZATION_INFO, UPDATE_EMPLOYEE_MAIN_HR_INFORMATION]) && (
              <UnitedTechnology />
            )}
            {isGranted(UPDATE_EMPLOYEE_SPECIALIZATION_INFO) && (
              <TextField
                label={<AppIconLabel icon="competencyLevel" tooltipTitle="Competency level" />}
                source="specializationInfo.competencyLevel"
              />
            )}
          </IconSectionRow>
        </Box>
      )}

      {isPositionSlotVisible && !isEditMode && (
        <EmployeePositionSlotField onPositionSlotIdChange={onPositionSlotIdChange} />
      )}
    </Box>
  );
};

export default SpecializationContent;
